import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle2, ArrowLeft, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { APPOINTMENT_TYPES, WHATSAPP_URL } from "@/data/clinic";
import { trackConversion, fireWhatsApp } from "@/lib/conversions";

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Only these three types can be booked online. Otros tratamientos requieren valoración previa.
const ALLOWED_SLUGS = APPOINTMENT_TYPES.map((a) => a.slug);

export default function Booking() {
  const { slug } = useParams();
  const nav = useNavigate();
  const initial = slug && ALLOWED_SLUGS.includes(slug) ? slug : "";
  const [appointmentType, setAppointmentType] = useState(initial);
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [patient, setPatient] = useState({ name: "", email: "", phone: "", notes: "" });
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setSlot("");
    api.get(`/slots?day=${fmtDate(date)}`)
      .then((r) => { setSlots(r.data.slots); setClosed(r.data.closed); })
      .catch(() => toast.error("No pudimos cargar los horarios. Vuelve a intentarlo en un momento."))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const canSubmit = appointmentType && date && slot && patient.name && patient.email && patient.phone && consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        treatment_slug: appointmentType,
        patient_name: patient.name.trim(),
        patient_email: patient.email.trim(),
        patient_phone: patient.phone.trim(),
        slot_date: fmtDate(date),
        slot_time: slot,
        notes: patient.notes,
      });
      setConfirmed(r.data);
      trackConversion("booking_complete", { appointment_type: appointmentType });
      toast.success("Solicitud de cita recibida");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No pudimos completar la reserva. Escríbenos por WhatsApp para ayudarte.";
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const minDate = useMemo(() => { const dd = new Date(); dd.setHours(0,0,0,0); return dd; }, []);
  const disableFn = (dd) => dd < minDate || dd.getDay() === 0;

  if (confirmed) {
    const chosen = APPOINTMENT_TYPES.find((a) => a.slug === confirmed.treatment_slug);
    return (
      <section className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="booking-confirmed">
        <CheckCircle2 size={56} className="mx-auto text-[hsl(var(--clinic-primary))]" />
        <h1 className="mt-6 font-display text-4xl">Tu solicitud fue registrada</h1>
        <p className="mt-4 text-[hsl(var(--clinic-muted))]">
          Nos vemos el <strong>{confirmed.slot_date}</strong> a las <strong>{confirmed.slot_time}</strong> para tu {chosen?.label || "cita"}.
        </p>
        <p className="mt-3 text-sm text-[hsl(var(--clinic-muted))]">
          Enviamos un correo de confirmación a <strong>{confirmed.patient_email}</strong>. Si necesitas cambiar el horario, escríbenos por WhatsApp.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => fireWhatsApp(WHATSAPP_URL, "booking-confirmed")} data-testid="booking-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white">
            <MessageCircle size={16} /> Escríbenos por WhatsApp
          </a>
          <button onClick={() => nav("/")} data-testid="booking-back-home" className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--clinic-border))] px-6 py-3 text-sm font-semibold">
            <ArrowLeft size={16} /> Volver al inicio
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="booking-page" className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
      <div className="mb-10 max-w-2xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">Agenda</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">Reserva tu cita</h1>
        <p className="mt-4 text-[hsl(var(--clinic-muted))]">
          Atendemos con cita previa de lunes a viernes de 8:00 a. m. a 6:00 p. m. y sábados de 8:00 a. m. a 1:30 p. m.
        </p>
        <p className="mt-2 text-sm text-[hsl(var(--clinic-muted))]">
          Las citas para otros tratamientos se programan después de una valoración, para poder recomendarte la opción adecuada.
        </p>
      </div>

      <form onSubmit={submit} className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
        <div className="rounded-3xl border border-[hsl(var(--clinic-border))] bg-white p-6">
          <fieldset className="block">
            <legend className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Tipo de cita</legend>
            <div className="mt-2 grid gap-3">
              {APPOINTMENT_TYPES.map((a) => (
                <label
                  key={a.slug}
                  htmlFor={`appt-${a.slug}`}
                  data-testid={`appt-type-${a.slug}`}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                    appointmentType === a.slug
                      ? "border-[hsl(var(--clinic-secondary))] bg-[hsl(var(--clinic-secondary))]/5"
                      : "border-[hsl(var(--clinic-border))] hover:border-[hsl(var(--clinic-text))]"
                  }`}
                >
                  <input
                    type="radio"
                    id={`appt-${a.slug}`}
                    name="appointment-type"
                    value={a.slug}
                    checked={appointmentType === a.slug}
                    onChange={(e) => { setAppointmentType(e.target.value); trackConversion("booking_start", { appointment_type: e.target.value }); }}
                    className="mt-1 accent-[hsl(var(--clinic-secondary))]"
                  />
                  <div>
                    <div className="text-sm font-semibold">{a.label}</div>
                    <div className="mt-1 text-xs text-[hsl(var(--clinic-muted))]">{a.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Día</div>
            <div className="mt-2" data-testid="booking-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={disableFn}
                className="rounded-xl border border-[hsl(var(--clinic-border))]"
              />
            </div>
          </div>
          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Hora</div>
            {!date && <p className="mt-2 text-sm text-[hsl(var(--clinic-muted))]">Selecciona un día primero.</p>}
            {date && loadingSlots && <p className="mt-2 text-sm">Cargando horarios…</p>}
            {date && !loadingSlots && closed && <p className="mt-2 text-sm text-[hsl(var(--clinic-secondary))]">Este día no atendemos.</p>}
            {date && !loadingSlots && !closed && (
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {slots.length === 0 && <span className="col-span-full text-sm">Sin disponibilidad para este día. Prueba con otro.</span>}
                {slots.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setSlot(s)}
                    data-testid={`slot-${s}`}
                    className={`rounded-full border px-3 py-2 text-sm ${
                      slot === s
                        ? "border-[hsl(var(--clinic-secondary))] bg-[hsl(var(--clinic-secondary))] text-white"
                        : "border-[hsl(var(--clinic-border))] hover:border-[hsl(var(--clinic-text))]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-[hsl(var(--clinic-border))] bg-white p-6">
          <h2 className="font-display text-2xl">Tus datos</h2>
          <div className="mt-4 grid gap-4">
            <input required aria-label="Nombre completo" placeholder="Nombre completo" data-testid="booking-name"
              value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className="input" />
            <input required type="email" aria-label="Correo" placeholder="Correo" data-testid="booking-email"
              value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} className="input" />
            <input required aria-label="Teléfono o WhatsApp" placeholder="Teléfono / WhatsApp" data-testid="booking-phone"
              value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} className="input" />
            <textarea rows={4} aria-label="Motivo o notas" placeholder="Motivo o notas (opcional)" data-testid="booking-notes"
              value={patient.notes} onChange={(e) => setPatient({ ...patient, notes: e.target.value })} className="input resize-none" />
            <label className="flex items-start gap-3 text-xs text-[hsl(var(--clinic-muted))]">
              <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="booking-consent" className="mt-1 accent-[hsl(var(--clinic-secondary))]" />
              <span>Autorizo el tratamiento de mis datos personales para gestionar la cita, según nuestra <a href="/legal" className="underline">política de privacidad</a>.</span>
            </label>
          </div>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="booking-submit-btn"
            className="mt-6 w-full rounded-full bg-[hsl(var(--clinic-secondary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? "Confirmando…" : "Solicitar cita"}
          </button>
          <p className="mt-3 text-xs text-[hsl(var(--clinic-muted))]">
            Confirmaremos la disponibilidad por correo o WhatsApp. Si necesitas ayuda antes de agendar, escríbenos.
          </p>
        </div>
      </form>
      <style>{`.input{width:100%;border:1px solid hsl(var(--clinic-border));border-radius:0.75rem;padding:0.75rem 1rem;font-size:0.9rem;background:white}.input:focus{outline:none;border-color:hsl(var(--clinic-primary));box-shadow:0 0 0 3px hsl(var(--clinic-primary)/0.15)}`}</style>
    </section>
  );
}
